import { useEffect, useMemo, useRef } from "react";
import "xterm/css/xterm.css";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { imagineStore } from "@/stores/imagine.store";

export function ConsoleLogsTab() {
  const clearConsoleLogs = imagineStore.use.consoleLogs.reset();
  const sandboxClient = imagineStore.use.sandboxState.sandboxClient();
  const logs = imagineStore.use.consoleLogs.logs();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const wasAtBottomRef = useRef(true); // Track if user was at bottom before new content

  // Auto scroll to bottom when new logs are added (only if user was at bottom)
  useEffect(() => {
    if (scrollContainerRef.current && logs.length > 0) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (scrollContainerRef.current && wasAtBottomRef.current) {
          scrollContainerRef.current.scrollTop =
            scrollContainerRef.current.scrollHeight;
        }
      });
    }
  }, [logs]);

  // Track scroll position to know if user is at bottom
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const isAtBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 10;
      wasAtBottomRef.current = isAtBottom;
    }
  };

  // Subscribe to iframe post messages
  const clearLogs = () => {
    clearConsoleLogs();
    sandboxClient?.clearLogs();
  };

  const renderRawLogSegment = (item: unknown) => {
    if (typeof item === "string") {
      return <span>{item}</span>;
    }

    if (typeof item === "object") {
      return <span>{JSON.stringify(item, null, 2)}</span>;
    }
  };

  const sortedLogs = useMemo(() => {
    return [...logs].sort(
      (a, b) =>
        new Date(a.logged_at).getTime() - new Date(b.logged_at).getTime(),
    );
  }, [logs]);

  return (
    <div className="h-full w-full bg-neutral-800 relative">
      <div
        ref={scrollContainerRef}
        className="h-full w-full overflow-y-scroll"
        onScroll={handleScroll}
      >
        {sortedLogs.length === 0 ? (
          <div className="flex items-center justify-center h-full text-neutral-400 text-sm">
            No console logs yet
          </div>
        ) : (
          sortedLogs.map((log) => (
            <div
              key={log.id}
              className={cn(
                `text-white font-mono text-sm hover:bg-neutral-700 px-3 py-1 w-full border-b border-neutral-700 text-[13px] whitespace-pre-wrap tracking-tight space-y-1.5`,
                log.level === "error" && "text-red-400",
                log.level === "warn" && "text-yellow-500",
                log.level === "log" && "text-neutral-200",
              )}
            >
              {log.raw.map((item: unknown, idx: number) => (
                <div key={`${log.id}-${idx}`}>{renderRawLogSegment(item)}</div>
              ))}
            </div>
          ))
        )}
      </div>
      <div className="absolute top-0 right-0 py-1.5 pr-5">
        {logs.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearLogs}
            className="bg-neutral-950 text-white h-6 text-xs hover:bg-neutral-900 hover:text-neutral-300"
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}
